import React, { useEffect, useRef, useState } from 'react';
import { useDemo, DemoRole } from '../context/DemoContext';
import './Demo.css';

// What to try in each seat — the guided part of the demo.
const TIPS: Record<DemoRole, string[]> = {
  student: [
    'Open Cross Body Lead and slow it to 0.5× — then press R to mirror it.',
    'Press A where the footwork starts and again where it ends to loop just that section.',
    'Check your profile: instructors have already left you feedback.',
  ],
  instructor: [
    'Work through the review queue — students are waiting on feedback.',
    'Watch a submission at 0.5×, then reply with one concrete correction.',
    'See the live class you have scheduled under Live.',
  ],
  owner: [
    'Add a move to the curriculum under Moves.',
    'Invite an instructor or student from Invites.',
    'Approve or feature a student demonstration under Demonstrations.',
  ],
};

const ROLE_NAME: Record<DemoRole, string> = {
  student: 'student',
  instructor: 'instructor',
  owner: 'studio owner',
};

interface DemoBarProps {
  /**
   * Pin the bar to the top of the viewport and publish its height as
   * --demo-bar-h on <html>, so fixed chrome (the admin sidebar and its mobile
   * hamburger) can sit below it instead of covering it.
   */
  sticky?: boolean;
}

/** Thin banner shown while a demo account is signed in: who you are, tips, switch seats. */
const DemoBar: React.FC<DemoBarProps> = ({ sticky = false }) => {
  const { accounts, currentRole, entering, enterAs } = useDemo();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visible = !!accounts && !!currentRole;

  useEffect(() => {
    const el = ref.current;
    if (!sticky || !visible || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--demo-bar-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--demo-bar-h');
    };
  }, [sticky, visible]);

  if (!visible) return null;
  const me = accounts.find((a) => a.role === currentRole)!;
  const others = accounts.filter((a) => a.role !== currentRole);

  return (
    <div ref={ref} className={`demo-bar ${sticky ? 'demo-bar--sticky' : ''}`} role="region" aria-label="Demo controls">
      <div className="demo-bar-row">
        <span className="demo-bar-badge">Demo</span>
        <span className="demo-bar-who">
          You're <strong>{me.name}</strong>, {ROLE_NAME[currentRole]}
        </span>
        <button
          type="button"
          className="demo-bar-link"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          What to try
        </button>
        <span className="demo-bar-note">Sample content: AI-generated videos, not checked for technique</span>
        <span className="demo-bar-spacer" />
        <span className="demo-bar-switch">
          Switch to
          {others.map((a) => (
            <button
              key={a.role}
              type="button"
              className="demo-bar-chip"
              onClick={() => enterAs(a.role)}
              disabled={entering !== null}
            >
              {entering === a.role ? '…' : ROLE_NAME[a.role]}
            </button>
          ))}
        </span>
      </div>
      {open && (
        <ol className="demo-bar-tips">
          {TIPS[currentRole].map((t) => <li key={t}>{t}</li>)}
        </ol>
      )}
    </div>
  );
};

export default DemoBar;
